import * as React from 'react';
import './GuestVouchers.css';

export interface GuestVoucherDto {
    guestVoucherId: string;
    voucherNumber: number;
    shortCode: string;
    reservationId: string;
    reservationProgramId: string;
    personId: string;
    personName: string | null;
    voucherType: string;
    windowStart: string;
    windowEnd: string;
    windowIndex: number;
    setPosition: number;
    setTotal: number;
    status: string;
    source: string;
    ptExpiryDate: string | null;
    programStartDate: string | null;
    programEndDate: string | null;
    bookingId: string | null;
    orderId: string | null;
    orderLineItemId: string | null;
    redeemedServiceType: string | null;
    redeemedAt: string | null;
    externalRedemption: boolean;
    externalMassageType: string | null;
    externalMassagePrice: number | null;
    externalReceiptDate: string | null;
    printedAt: string | null;
    printCount: number;
    flagReason: string | null;
    createdUtc: string;
    deletedUtc: string | null;
    deletedReason: string | null;
    activeFlowId: string | null;
}

export interface GuestVoucherLogDto {
    guestVoucherLogId: string;
    action: string;
    details: string | null;
    createdUtc: string;
    createdByUserId: string;
    createdByUserName: string | null;
}

interface GuestVoucherCardProps {
    voucher: GuestVoucherDto;
    companyManagerBaseUrl?: string | null;
    isDeleted?: boolean;
    onConvert?: (voucher: GuestVoucherDto) => void;
    onPrint?: (voucher: GuestVoucherDto) => void;
    onResolveFlag?: (voucher: GuestVoucherDto) => void;
    onRevert?: (voucher: GuestVoucherDto) => void;
    onVoid?: (voucher: GuestVoucherDto) => void;
    onUnvoid?: (voucher: GuestVoucherDto) => void;
}

function formatDate(iso: string | null): string {
    if (!iso) return '\u2014';
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(iso: string | null): string {
    if (!iso) return '\u2014';
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const ACTION_COLORS: Record<string, string> = {
    Created: '#059669', Converted: '#3b82f6', Reverted: '#d97706', Reconciled: '#0891b2',
    RevertedReconciliation: '#d97706', Expired: '#6b7280', Cancelled: '#ef4444', Flagged: '#0891b2',
    FlagResolved: '#059669', ManualCreated: '#059669', OverrideChanged: '#6b7280', Voided: '#374151',
    Unvoided: '#059669', Deleted: '#ef4444', Updated: '#6b7280', UpdatedReconciliation: '#0891b2',
};

export const GuestVoucherCard: React.FC<GuestVoucherCardProps> = ({
    voucher, companyManagerBaseUrl, isDeleted, onConvert, onPrint, onResolveFlag, onRevert, onVoid, onUnvoid,
}) => {
    const [showHistory, setShowHistory] = React.useState(false);
    const [logs, setLogs] = React.useState<GuestVoucherLogDto[]>([]);
    const [logsLoading, setLogsLoading] = React.useState(false);

    const isRed = voucher.voucherType === 'Red';
    const accentColor = isRed ? '#ef4444' : '#3b82f6';
    const hasFlow = !!voucher.activeFlowId;

    const loadLogs = async () => {
        setLogsLoading(true);
        try {
            const r = await fetch(`/api/sales/guest-vouchers/${voucher.guestVoucherId}/logs`);
            if (r.ok) setLogs(await r.json());
        } catch { /* ignore */ }
        finally { setLogsLoading(false); }
    };

    const toggleHistory = () => {
        const next = !showHistory;
        setShowHistory(next);
        if (next && logs.length === 0) loadLogs();
    };

    return (
        <div className={`gv-card ${isDeleted ? 'gv-card-deleted' : ''}`} style={{ borderTopColor: accentColor }}>
            {/* Top section: number + badges */}
            <div className="gv-card-top">
                <div className="gv-card-id">
                    <span className={`gv-card-number ${isDeleted ? 'gv-card-number-deleted' : ''}`}>
                        V-{voucher.voucherNumber}
                    </span>
                    <span className="gv-card-type" style={{ backgroundColor: accentColor }}>
                        {voucher.voucherType}
                    </span>
                    <span className="gv-card-pos">{voucher.setPosition}/{voucher.setTotal}</span>
                </div>
                <div className="gv-card-status-area">
                    {isDeleted ? (
                        <span className="gv-status gv-status-deleted">Deleted</span>
                    ) : (
                        <span className={`gv-status gv-status-${voucher.status.toLowerCase()}`}>{voucher.status}</span>
                    )}
                    {hasFlow && (
                        <span className="gv-status gv-status-flow">
                            <span className="spinner-border spinner-border-sm" style={{ width: '0.55em', height: '0.55em' }} />
                        </span>
                    )}
                    {voucher.source === 'Manual' && <span className="gv-status gv-status-adhoc">Ad-hoc</span>}
                </div>
            </div>

            {/* Details section */}
            <div className="gv-card-details">
                <div className="gv-detail-row">
                    <i className="fa-regular fa-calendar"></i>
                    <span>
                        {formatDate(voucher.programStartDate ?? voucher.windowStart)} &mdash; {formatDate(voucher.programEndDate ?? voucher.windowEnd)}
                    </span>
                </div>
                {isRed && (
                    <div className="gv-detail-row gv-detail-pt">
                        <i className="fa-solid fa-stopwatch"></i>
                        <span>PT: {formatDate(voucher.windowStart)} &mdash; {formatDate(voucher.windowEnd)}</span>
                    </div>
                )}
                <div className="gv-detail-row gv-detail-services">
                    <i className="fa-solid fa-concierge-bell"></i>
                    <span>
                        {isRed
                            ? 'PT \u00B7 Personal Consultation \u00B7 Muay Thai 1-on-1 \u00B7 Massage'
                            : '1hr Massage (off-site) \u00B7 Muay Thai 1-on-1'}
                    </span>
                </div>
            </div>

            {/* Redemption info */}
            {voucher.status === 'Used' && voucher.redeemedServiceType && !voucher.externalRedemption && (
                <div className="gv-redeemed-info">
                    <div className="gv-redeemed-service">
                        <i className="fa-solid fa-check-circle"></i>
                        {voucher.redeemedServiceType}
                        {voucher.redeemedAt && <span className="gv-redeemed-date"> &mdash; {formatDate(voucher.redeemedAt)}</span>}
                    </div>
                    {companyManagerBaseUrl && voucher.bookingId && (
                        <div className="gv-redeemed-links">
                            <a href={`${companyManagerBaseUrl}/people/${voucher.personId}/bookings/${voucher.bookingId}`} target="_blank" rel="noopener noreferrer">
                                Booking <i className="fa-solid fa-arrow-up-right-from-square"></i>
                            </a>
                            {voucher.orderId && (
                                <a href={`${companyManagerBaseUrl}/people/${voucher.personId}/orders/${voucher.orderId}`} target="_blank" rel="noopener noreferrer">
                                    Order <i className="fa-solid fa-arrow-up-right-from-square"></i>
                                </a>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* External massage info */}
            {voucher.status === 'Used' && voucher.externalRedemption && (
                <div className="gv-redeemed-info gv-redeemed-external">
                    <div className="gv-redeemed-service">
                        <i className="fa-solid fa-spa"></i>
                        {voucher.externalMassageType} &mdash; {voucher.externalMassagePrice?.toLocaleString()} THB
                        {voucher.externalReceiptDate && <span className="gv-redeemed-date"> (receipt {formatDate(voucher.externalReceiptDate)})</span>}
                    </div>
                </div>
            )}

            {/* Flag info */}
            {voucher.status === 'Flagged' && voucher.flagReason && (
                <div className="gv-flag-info">
                    <i className="fa-solid fa-flag"></i>
                    {voucher.flagReason}
                </div>
            )}

            {/* Deleted reason */}
            {isDeleted && voucher.deletedReason && (
                <div className="gv-deleted-reason">
                    <i className="fa-solid fa-trash"></i>
                    {voucher.deletedReason}
                </div>
            )}

            {/* Action bar */}
            {!isDeleted && (
                <div className="gv-card-actions">
                    <a className="gv-action" href={`/v/${voucher.shortCode}`} target="_blank" rel="noopener noreferrer" title="View voucher page">
                        <i className="fa-solid fa-eye"></i>
                    </a>
                    {hasFlow && (voucher.status === 'Used' ? onRevert : onConvert) && (
                        <button className="gv-action gv-action-primary" onClick={() => voucher.status === 'Used' ? onRevert?.(voucher) : onConvert?.(voucher)} title="View progress">
                            <i className="fa-solid fa-bars-progress"></i>
                        </button>
                    )}
                    {!hasFlow && voucher.status === 'Active' && onConvert && (
                        <button className="gv-action gv-action-primary" onClick={() => onConvert(voucher)} title="Convert to order">
                            <i className="fa-solid fa-arrow-right-arrow-left"></i>
                        </button>
                    )}
                    {!hasFlow && voucher.status === 'Active' && onPrint && (
                        <button className="gv-action" onClick={() => onPrint(voucher)} title={voucher.printCount > 0 ? `Reprint (${voucher.printCount}x)` : 'Print'}>
                            <i className="fa-solid fa-print"></i>
                        </button>
                    )}
                    {!hasFlow && voucher.status === 'Active' && onVoid && (
                        <button className="gv-action gv-action-danger" onClick={() => onVoid(voucher)} title="Void">
                            <i className="fa-solid fa-ban"></i>
                        </button>
                    )}
                    {!hasFlow && voucher.status === 'Voided' && onUnvoid && (
                        <button className="gv-action gv-action-success" onClick={() => onUnvoid(voucher)} title="Restore">
                            <i className="fa-solid fa-rotate-left"></i>
                        </button>
                    )}
                    {!hasFlow && voucher.status === 'Used' && onRevert && (
                        <button className="gv-action gv-action-warning" onClick={() => onRevert(voucher)} title="Revert conversion">
                            <i className="fa-solid fa-undo"></i>
                        </button>
                    )}
                    {!hasFlow && voucher.status === 'Flagged' && onResolveFlag && (
                        <button className="gv-action gv-action-info" onClick={() => onResolveFlag(voucher)} title="Resolve flag">
                            <i className="fa-solid fa-flag-checkered"></i>
                        </button>
                    )}
                    <button className="gv-action gv-action-history" onClick={toggleHistory} title="History">
                        <i className="fa-solid fa-clock-rotate-left"></i>
                    </button>
                </div>
            )}

            {/* History panel */}
            {showHistory && (
                <div className="gv-history-panel">
                    {logsLoading ? (
                        <div className="gv-history-empty">Loading...</div>
                    ) : logs.length === 0 ? (
                        <div className="gv-history-empty">No history entries.</div>
                    ) : (
                        logs.map(log => (
                            <div key={log.guestVoucherLogId} className="gv-history-entry">
                                <span className="gv-history-dot" style={{ backgroundColor: ACTION_COLORS[log.action] || '#6b7280' }}></span>
                                <div className="gv-history-content">
                                    <div className="gv-history-action">
                                        <strong>{log.action}</strong>
                                        <span className="gv-history-time">{formatDateTime(log.createdUtc)}</span>
                                    </div>
                                    <div className="gv-history-meta">
                                        {log.createdByUserName || '(system)'}
                                        {log.details && <span className="gv-history-details"> &mdash; {log.details}</span>}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};
